import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ArrowRight, Clock, X, TrendingUp, User } from "lucide-react";
import api, { formatRON, logSearch, mediaUrl } from "@/lib/api";
import { getRecent, addRecent, removeRecent, clearRecent } from "@/lib/recentSearches";
import MicButton from "@/components/MicButton";

export default function SearchAutocomplete({
  onClose,
  autoFocus = false,
  variant = "header",
  popularTags = [],
  placeholder = "Caută produse, experți, anunțuri...",
}) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [expertSuggestions, setExpertSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [searched, setSearched] = useState(false);
  const [recent, setRecent] = useState([]);
  const [topCats, setTopCats] = useState([]);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setRecent(getRecent());
    api.get("/categories")
      .then((r) => setTopCats(r.data.filter((c) => c.count > 0).slice(0, 6)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setSuggestions([]);
      setExpertSuggestions([]);
      setSearched(false);
      return;
    }
    const t = setTimeout(() => {
      Promise.all([
        api.get("/products", { params: { search: term } }).then((r) => r.data).catch(() => []),
        api.get("/experts", { params: { search: term } }).then((r) => r.data).catch(() => []),
      ]).then(([prods, experts]) => {
        setSuggestions(prods.slice(0, 6));
        setExpertSuggestions(experts.slice(0, 4));
        setSearched(true); setOpen(true); setActive(-1);
      });
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const commit = (term) => {
    const t = (term ?? q).trim();
    setOpen(false);
    if (t.length >= 2) { addRecent(t); setRecent(getRecent()); logSearch(t); }
    if (onClose) onClose();
    navigate(t ? `/produse?search=${encodeURIComponent(t)}` : "/produse");
  };

  const goToProduct = (id) => {
    setOpen(false);
    if (onClose) onClose();
    navigate(`/produse/${id}`);
  };

  const goToExpert = (id) => {
    setOpen(false);
    if (onClose) onClose();
    navigate(`/expert/${id}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (open && active >= 0 && suggestions[active]) goToProduct(suggestions[active].id);
      else commit();
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? suggestions.length - 1 : a - 1)); }
    else if (e.key === "Escape") { setOpen(false); if (onClose) onClose(); }
  };

  const term = q.trim();
  const showRecentPanel = open && term.length < 2 && recent.length > 0;
  const hasResults = suggestions.length > 0 || expertSuggestions.length > 0;
  const showResults = open && term.length >= 2 && hasResults;
  const showNoResults = open && term.length >= 2 && searched && !hasResults;
  const isHero = variant === "hero";
  const inputH = isHero ? "h-14" : "h-12";

  return (
    <div className="relative w-full" ref={boxRef}>
      <div className={isHero ? "flex flex-col sm:flex-row gap-3" : ""}>
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setOpen(true)}
            autoComplete="off"
            placeholder={placeholder}
            className={`w-full ${inputH} pl-12 pr-12 bg-white text-slate-900 placeholder:text-slate-400 text-base border border-slate-300 outline-none focus:ring-2 focus:ring-orange-500`}
            data-testid="search-input-ac"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
            <MicButton
              testid="search-mic"
              onInterim={(txt) => { setQ(txt); setOpen(true); }}
              onResult={(txt) => { setQ(txt); setOpen(true); }}
            />
          </div>
        </div>
        {isHero && (
          <button type="button" onClick={() => commit()} className="h-14 px-8 bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide text-sm transition-colors inline-flex items-center justify-center gap-2 shrink-0" data-testid="hero-search-button">
            <Search className="w-4 h-4" /> Caută
          </button>
        )}
      </div>

      {(showRecentPanel || showResults || showNoResults) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 shadow-2xl z-50 text-left max-h-[380px] overflow-y-auto" data-testid="search-suggestions">
          {showRecentPanel && (
            <div>
              <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
                <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Căutări recente</span>
                <button type="button" onClick={() => { clearRecent(); setRecent([]); }} className="text-[10px] uppercase tracking-wide text-slate-400 hover:text-orange-600" data-testid="clear-recent">Șterge</button>
              </div>
              {recent.map((t) => (
                <div key={t} className="flex items-center hover:bg-slate-50">
                  <button type="button" onClick={() => { setQ(t); commit(t); }} className="flex-1 flex items-center gap-3 px-3 py-2 text-left text-sm text-slate-700" data-testid={`recent-${t}`}>
                    <Clock className="w-4 h-4 text-slate-400 shrink-0" /> {t}
                  </button>
                  <button type="button" onClick={() => { removeRecent(t); setRecent(getRecent()); }} className="px-3 text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          {showResults && (
            <div>
              {suggestions.length > 0 && (
                <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Produse & anunțuri</div>
              )}
              {suggestions.map((s, i) => (
                <button type="button" key={s.id} onMouseEnter={() => setActive(i)} onClick={() => (s.type === "listing" ? navigate(`/anunt/${s.id}`) : goToProduct(s.id))}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${i === active ? "bg-slate-100" : "hover:bg-slate-50"}`}
                  data-testid={`suggestion-${i}`}>
                  <img src={mediaUrl(s.image)} alt="" className="w-10 h-10 object-cover bg-slate-100 border border-slate-200 shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-900 truncate">{s.name}</span>
                    <span className="block text-xs text-slate-400">{s.type === "listing" ? "Anunț utilizator" : (s.brand || "OXO")}</span>
                  </span>
                  <span className="text-sm font-bold text-slate-900 shrink-0">{s.price ? formatRON(s.price) : "La cerere"}</span>
                </button>
              ))}

              {expertSuggestions.length > 0 && (
                <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-orange-500 border-t border-slate-100 mt-1">Experți</div>
              )}
              {expertSuggestions.map((e) => (
                <button type="button" key={e.id} onClick={() => goToExpert(e.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`expert-suggestion-${e.id}`}>
                  <span className="w-10 h-10 bg-orange-50 border border-orange-200 shrink-0 flex items-center justify-center"><User className="w-4 h-4 text-orange-600" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-900 truncate">{e.name}{e.senior ? " · Veteran" : ""}</span>
                    <span className="block text-xs text-slate-400 truncate">{(e.specializations || []).slice(0, 2).join(" · ") || "Expert OXO"}</span>
                  </span>
                  {e.endorsed && <span className="text-[9px] uppercase font-bold text-orange-600 shrink-0">Endorsed</span>}
                </button>
              ))}

              <button type="button" onClick={() => commit()} className="w-full flex items-center justify-between gap-2 px-3 py-3 text-sm font-bold uppercase tracking-wide text-orange-600 hover:bg-orange-50 border-t border-slate-100 transition-colors" data-testid="suggestion-see-all">
                Vezi toate rezultatele pentru „{term}" <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {showNoResults && (
            <div className="p-4" data-testid="no-results-hint">
              <p className="text-sm text-slate-600">Niciun rezultat pentru <span className="font-bold">„{term}"</span>.</p>
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mt-4 mb-2">Răsfoiește categoriile</p>
              <div className="flex flex-wrap gap-2">
                {topCats.map((c) => (
                  <Link key={c.slug} to={`/produse?cat=${c.slug}`} onClick={() => { setOpen(false); if (onClose) onClose(); }}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 border border-slate-300 hover:border-orange-500 hover:text-orange-600 px-3 py-1.5 transition-colors">
                    {c.name.replace(/^\d+ · /, "")} <span className="text-slate-400">({c.count})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isHero && popularTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-xs uppercase tracking-wide text-slate-400 inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Populare:</span>
          {popularTags.map((t) => (
            <button key={t} type="button" onClick={() => { setQ(t); commit(t); }}
              className="text-xs font-semibold text-slate-200 border border-slate-600 hover:border-orange-500 hover:text-orange-400 px-3 py-1.5 transition-colors" data-testid={`hero-tag-${t}`}>
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
